import { useCallback, useEffect, useRef, useState } from 'react'

import {
  FilterList,
  Refresh,
  Search,
  EventNote,
} from '@mui/icons-material'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Alert,
  Skeleton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Stack,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
} from '@mui/material'

import { eventLogsService, type EventLog, handleApiError } from '../api'
import { EventLogTable } from '../components/event-logs/event-log-table'

const formatDateForInput = (date: Date): string => {
  return date.toISOString().split('T')[0] || ''
}

const getStartOfDay = (date: Date): Date => {
  const start = new Date(date)
  start.setHours(0, 0, 0, 0)
  return start
}

const getEndOfDay = (date: Date): Date => {
  const end = new Date(date)
  end.setHours(23, 59, 59, 999)
  return end
}

const getYesterday = (): Date => {
  const yesterday = new Date()
  yesterday.setDate(yesterday.getDate() - 1)
  return yesterday
}

type DateFilter = 'all' | 'today' | 'yesterday' | 'custom'

export default function SystemEventLogsPage() {
  const [events, setEvents] = useState<EventLog[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [dateFilter, setDateFilter] = useState<DateFilter>('today')
  const [customStartDate, setCustomStartDate] = useState(formatDateForInput(new Date()))
  const [customEndDate, setCustomEndDate] = useState(formatDateForInput(new Date()))
  const [searchQuery, setSearchQuery] = useState('')
  const [activeSearch, setActiveSearch] = useState('')
  const [customDatesVersion, setCustomDatesVersion] = useState(0)
  const customStartRef = useRef(customStartDate)
  const customEndRef = useRef(customEndDate)
  customStartRef.current = customStartDate
  customEndRef.current = customEndDate
  const requestIdRef = useRef(0)

  const fetchEvents = useCallback(async () => {
    const currentRequestId = ++requestIdRef.current

    try {
      setIsLoading(true)
      setError('')

      let params: { startDate?: string; endDate?: string; search?: string } = {}

      if (dateFilter === 'today') {
        const today = new Date()
        params = {
          startDate: getStartOfDay(today).toISOString(),
          endDate: getEndOfDay(today).toISOString(),
        }
      } else if (dateFilter === 'yesterday') {
        const yesterday = getYesterday()
        params = {
          startDate: getStartOfDay(yesterday).toISOString(),
          endDate: getEndOfDay(yesterday).toISOString(),
        }
      } else if (dateFilter === 'custom') {
        const startDate = new Date(customStartRef.current)
        const endDate = new Date(customEndRef.current)
        params = {
          startDate: getStartOfDay(startDate).toISOString(),
          endDate: getEndOfDay(endDate).toISOString(),
        }
      }

      if (activeSearch) {
        params.search = activeSearch
      }

      const fetchedEvents = await eventLogsService.getAllEventLogs(params)
      if (requestIdRef.current !== currentRequestId) return
      setEvents(fetchedEvents)
    } catch (err) {
      if (requestIdRef.current !== currentRequestId) return
      setError(handleApiError(err))
    } finally {
      if (requestIdRef.current === currentRequestId) {
        setIsLoading(false)
      }
    }
  }, [dateFilter, activeSearch, customDatesVersion])

  useEffect(() => {
    fetchEvents()
  }, [fetchEvents])

  const handleApplyCustomDates = () => {
    setCustomDatesVersion((v) => v + 1)
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <EventNote sx={{ fontSize: 28, color: 'primary.main' }} />
          <Box>
            <Typography variant="h5" fontWeight={600}>
              System Event Logs
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Monitor and search system-wide activity
            </Typography>
          </Box>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={fetchEvents} disabled={isLoading} size="small">
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
              <FilterList fontSize="small" />
              <Typography variant="body2" fontWeight={500}>
                Filters
              </Typography>
            </Box>

            <TextField
              placeholder="Search events, messages, or users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setActiveSearch(searchQuery)
                }
              }}
              size="small"
              sx={{ minWidth: 300 }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <Button
              variant="contained"
              size="small"
              onClick={() => setActiveSearch(searchQuery)}
              disableElevation
            >
              Search
            </Button>

            <FormControl sx={{ minWidth: 180 }} size="small">
              <InputLabel>Date Range</InputLabel>
              <Select
                value={dateFilter}
                label="Date Range"
                onChange={(e) => setDateFilter(e.target.value as DateFilter)}
              >
                <MenuItem value="all">All Time</MenuItem>
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="yesterday">Yesterday</MenuItem>
                <MenuItem value="custom">Custom Range</MenuItem>
              </Select>
            </FormControl>

            {dateFilter === 'custom' && (
              <>
                <TextField
                  label="Start Date"
                  type="date"
                  size="small"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="End Date"
                  type="date"
                  size="small"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <Button variant="outlined" size="small" onClick={handleApplyCustomDates} disableElevation>
                  Apply
                </Button>
              </>
            )}
          </Stack>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {isLoading ? (
        <Card variant="outlined">
          <CardContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Skeleton variant="rectangular" height={48} sx={{ borderRadius: 1 }} />
              <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
              <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
              <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
              <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
            </Box>
          </CardContent>
        </Card>
      ) : error ? null : events.length === 0 ? (
        <Card variant="outlined">
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <EventNote sx={{ fontSize: 48, color: 'text.disabled', mb: 1.5 }} />
            <Typography variant="body1" color="text.secondary" fontWeight={500}>
              No events found
            </Typography>
            <Typography variant="body2" color="text.disabled">
              Try adjusting your filters or date range
            </Typography>
          </Box>
        </Card>
      ) : (
        <Card variant="outlined">
          <Box sx={{ px: 2.5, py: 1.5, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="subtitle2" color="text.secondary">
              {events.length} {events.length === 1 ? 'event' : 'events'}
            </Typography>
          </Box>
          <EventLogTable
            events={events}
            columns={['time', 'level', 'event', 'message', 'actor', 'user', 'link']}
          />
        </Card>
      )}
    </Box>
  )
}
